/**
 * Copy-to-clipboard hook with visual feedback.
 *
 * Provides temporary "copied" state since clipboard operations are silent.
 * Can be used to provide feedback to the user when the text is copied to the clipboard.
 * Uses timeout ref to handle rapid clicks - clears existing timeout before
 * setting new one to prevent flickering feedback state.
 *
 * @param options - Optional configuration object
 * @param options.delay - Duration in ms to show feedback (default: 800ms)
 * @returns `{ isCopied, copy }` - boolean state and copy function
 */

import { useCallback, useRef, useState } from 'react';
import { useCopyToClipboard } from '@bridebook/ui';

type UseCopyToClipboardWithFeedbackOptions = {
  delay?: number;
};

export const useCopyToClipboardWithFeedback = (
  options: UseCopyToClipboardWithFeedbackOptions = {},
) => {
  const { delay = 800 } = options;
  const { copy } = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleCopyLink = useCallback(
    (text: string) => {
      copy(text);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsCopied(true);
      timeoutRef.current = setTimeout(() => {
        setIsCopied(false);
        timeoutRef.current = null;
      }, delay);
    },
    [copy, delay],
  );

  return { isCopied, copy: handleCopyLink };
};
